import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Download, BookOpen, FileText, BookMarked, Paperclip,
  ChevronRight, RefreshCw, X, Loader2, AlignLeft, Upload,
  CheckCircle2, Lock, Square, CheckCheck, RotateCcw, ShieldCheck, AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { getThesis, listChapters } from '../../lib/thesisApi'
import { applyWorkflowTransition } from '../../utils/chapterWorkflowEngine'
import { useAuthStore } from '../../store/authStore'
import CitationAuditPanel from '../../components/CitationAuditPanel'
import ThesisMemoryPanel from '../../components/ThesisMemoryPanel'
import LiteratureEcologyPanel from '../../components/LiteratureEcologyPanel'
import ReindexPanel from '../../components/ReindexPanel'
import ReferenceUploadPanel from '../../components/ReferenceUploadPanel'
import ResearchSourcesPanel from '../../components/ResearchSourcesPanel'
import { SectionProfileChip } from '../../components/SectionProfileBadge'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sectionCount(chapter) {
  const raw  = chapter.toc_sections?.sections
  const secs = Array.isArray(raw) ? raw : []
  const subsTotal = secs.reduce((sum, s) => sum + (s.subsections?.length || 0), 0)
  return secs.length + subsTotal
}

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS = {
  NOT_STARTED:   { label: 'Not Started',   color: '#94a3b8',  border: '#e2e8f0',          bg: 'rgba(226,232,240,0.4)'  },
  DRAFT:         { label: 'Draft',         color: '#2563eb',  border: '#2563eb',          bg: 'rgba(37,99,235,0.08)'  },
  GENERATING:    { label: 'Generating',    color: '#2563eb',  border: '#2563eb',          bg: 'rgba(37,99,235,0.05)', pulse: true },
  REVIEWED:      { label: 'Reviewed',      color: '#0891b2',  border: '#0891b2',          bg: 'rgba(8,145,178,0.08)'  },
  REVIEW:        { label: 'Under Review',  color: '#d97706',  border: '#d97706',          bg: 'rgba(217,119,6,0.08)'  },
  APPROVED:      { label: 'Approved',      color: '#16a34a',  border: '#16a34a',          bg: 'rgba(22,163,74,0.08)'  },
  FINALIZED:     { label: 'Finalized',     color: '#15803d',  border: '#15803d',          bg: 'rgba(21,128,61,0.10)', locked: true },
  REGEN_PENDING: { label: 'Regen Pending', color: '#dc2626',  border: '#dc2626',          bg: 'rgba(220,38,38,0.08)', alert: true  },
  LOCKED:        { label: 'Locked',        color: '#7c3aed',  border: '#7c3aed',          bg: 'rgba(124,58,237,0.08)', locked: true },
}

// ─── Progress ring ─────────────────────────────────────────────────────────────

function ProgressRing({ pct, size = 48 }) {
  const r   = (size - 6) / 2
  const circ = 2 * Math.PI * r
  const dash = circ * Math.min(pct / 100, 1)
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={5} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={pct >= 100 ? '#16a34a' : '#2563eb'}
        strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  )
}

// ─── Generation Slide-over ─────────────────────────────────────────────────────

function GenerateSlideOver({ chapter, onClose, onGenerated }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [notes,      setNotes]      = useState(chapter?.user_notes || '')
  const [generating, setGenerating] = useState(false)
  const [error,      setError]      = useState('')

  if (!chapter) return null

  const tocSections = chapter.toc_sections?.sections || chapter.toc_sections || []

  const handleGenerate = async () => {
    setGenerating(true)
    setError('')
    try {
      // Save notes directly — no placeholder injection
      try {
        await api.put(`/thesis/${id}/chapters/${chapter.id}`, { user_notes: notes })
      } catch {
        await supabase.from('chapters').update({ user_notes: notes }).eq('id', chapter.id)
      }

      onClose()
      onGenerated(chapter.id, 'GENERATING')
      navigate(`/app/thesis/${id}/chapters/${chapter.id}?streaming=1`)
    } catch (err) {
      setError(err.message || 'Generation failed')
      setGenerating(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40"
      style={{ background: 'rgba(0,0,0,0.3)' }}
      onClick={onClose}
    >
      <div
        className="absolute right-0 top-0 h-full flex flex-col overflow-hidden"
        style={{ width: 480, background: '#FFFFFF', borderLeft: '1px solid #DDE4EF' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5" style={{ borderBottom: '1px solid #DDE4EF' }}>
          <div>
            <p className="font-mono text-xs text-acdyon-textFaint mb-0.5">Chapter {chapter.chapter_number}</p>
            <h3 className="font-display text-xl text-acdyon-text leading-tight">{chapter.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-acdyon-surface rounded transition-colors mt-1">
            <X style={{ width: 16, height: 16, color: '#8EA4BC' }} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Section tree */}
          <div>
            <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-3">
              Chapter Structure
            </p>
            {tocSections.length === 0 ? (
              <p className="font-sans text-xs text-acdyon-textFaint">No sections defined in ToC.</p>
            ) : (
              <div className="space-y-1">
                {tocSections.map(sec => (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 py-1">
                      <span className="font-mono text-xs shrink-0" style={{ color: '#2563eb', minWidth: 28 }}>{sec.number}</span>
                      <span className="font-sans text-sm text-acdyon-textMuted">{sec.title}</span>
                      <span className="font-mono text-xs text-acdyon-textFaint ml-auto shrink-0">{sec.pages}p</span>
                    </div>
                    {sec.subsections?.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 py-0.5 ml-6">
                        <span className="font-mono text-xs shrink-0" style={{ color: '#8EA4BC', minWidth: 28 }}>{sub.number}</span>
                        <span className="font-sans text-xs text-acdyon-textFaint">{sub.title}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="font-sans text-sm font-medium text-acdyon-text block mb-2">
              Pre-generation Notes
            </label>
            <p className="font-sans text-xs text-acdyon-textFaint mb-2">
              Any specific instructions, data points, or sources to include?
            </p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="e.g. Focus heavily on Indian context for Section 2.3. Include the TAM and UTAUT frameworks..."
              className="w-full px-3 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none resize-none"
              style={{ background: '#F4F7FC', border: '1px solid #DDE4EF' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(37,99,235,0.4)')}
              onBlur={e => (e.target.style.borderColor = '#DDE4EF')}
            />
          </div>

          {/* Autonomous completion notice */}
          <div className="flex items-start gap-3 py-3 px-4 rounded-lg" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <span style={{ fontSize: 14, marginTop: 1 }}>✓</span>
            <div>
              <p className="font-sans text-sm font-medium" style={{ color: '#059669' }}>Autonomous completion active</p>
              <p className="font-sans text-xs mt-0.5" style={{ color: '#065f46' }}>
                The system infers all missing research context from computed outputs and domain profiles. No placeholders will appear in generated text.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg text-center" style={{ background: '#F4F7FC' }}>
              <p className="font-mono text-base font-semibold text-brand-600">{chapter.estimated_pages}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">Est. Pages</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: '#F4F7FC' }}>
              <p className="font-mono text-base font-semibold text-brand-600">{(chapter.target_words || 0).toLocaleString()}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">Target Words</p>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg font-sans text-sm" style={{ background: 'rgba(239,68,68,0.08)', color: '#F87171', border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4" style={{ borderTop: '1px solid #DDE4EF' }}>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full flex items-center justify-center gap-3 rounded-xl font-sans font-semibold text-sm transition-all disabled:opacity-60"
            style={{ background: '#2563eb', color: '#FFFFFF', height: 56 }}
          >
            {generating ? (
              <><Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> Connecting…</>
            ) : (
              <>Generate Full Chapter <ChevronRight style={{ width: 16, height: 16 }} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Chapter Card ─────────────────────────────────────────────────────────────

function ChapterCard({ chapter, onGenerate, onStop, selected, onToggleSelect, onUnapprove, unapproving }) {
  const navigate   = useNavigate()
  const { id }     = useParams()
  const [stopping, setStopping] = useState(false)
  const cfg        = STATUS[chapter.status] || STATUS.NOT_STARTED
  const progress   = chapter.target_words > 0 ? Math.min((chapter.word_count / chapter.target_words) * 100, 100) : 0
  const sections   = sectionCount(chapter)

  return (
    <div
      className="relative rounded-xl overflow-hidden flex flex-col"
      style={{
        background:   '#FFFFFF',
        border:       `1px solid ${cfg.border}`,
        borderLeft:   `4px solid ${cfg.border}`,
        padding:      '24px',
        ...(cfg.pulse ? { animation: 'chapterPulse 2s ease-in-out infinite' } : {}),
      }}
    >
      {/* Watermark chapter number */}
      <span
        className="absolute top-3 right-4 font-display select-none pointer-events-none"
        style={{ fontSize: 72, lineHeight: 1, color: 'rgba(37,99,235,0.04)', fontWeight: 700 }}
      >
        {String(chapter.chapter_number).padStart(2, '0')}
      </span>

      {/* Top row */}
      <div className="flex items-center gap-2 mb-3 relative z-10">
        {/* Ticking chapters narrows the header's approve/unapprove buttons to a
            subset; with none ticked they act on every eligible chapter. */}
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          title="Select for bulk approve or unapprove"
          className="accent-brand-600 cursor-pointer"
          style={{ width: 14, height: 14 }}
        />
        <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: `${cfg.color}18`, color: cfg.color }}>
          CH {String(chapter.chapter_number).padStart(2, '0')}
        </span>
        <span className="font-sans text-xs px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}40` }}>
          {cfg.label}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-sans font-semibold text-acdyon-text mb-1 relative z-10" style={{ fontSize: 18 }}>
        {chapter.title}
      </h3>
      <div className="flex items-center gap-2 mb-3 relative z-10">
        <SectionProfileChip chapterTitle={chapter.title} chapterNumber={chapter.chapter_number} />
        <p className="font-mono text-xs text-acdyon-textFaint">{sections} sections · ~{chapter.estimated_pages} pages</p>
      </div>

      {/* Progress bar */}
      <div className="mb-1">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#DDE4EF' }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progress}%`, background: progress >= 100 ? '#3DB87A' : '#2563eb' }}
          />
        </div>
        <p className="font-mono text-xs text-acdyon-textFaint mt-1.5">
          {(chapter.word_count || 0).toLocaleString()} / {(chapter.target_words || 0).toLocaleString()} words
        </p>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-2 mt-auto pt-4 relative z-10">
        {chapter.status === 'NOT_STARTED' && (
          <button
            onClick={() => onGenerate(chapter)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-all"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            Generate Chapter <ChevronRight style={{ width: 14, height: 14 }} />
          </button>
        )}
        {(chapter.status === 'DRAFT' || chapter.status === 'REGEN_PENDING') && (
          <>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
              style={{ border: '1px solid rgba(37,99,235,0.4)', color: '#2563eb' }}
            >
              {chapter.status === 'REGEN_PENDING' ? 'Regenerate' : 'Continue Editing'}
            </Link>
            {chapter.status !== 'REGEN_PENDING' && (
              <button
                onClick={() => onGenerate(chapter)}
                className="px-3 py-2 rounded-lg font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors"
                style={{ border: '1px solid #DDE4EF' }}
                title="Regenerate chapter"
              >
                <RefreshCw style={{ width: 13, height: 13 }} />
              </button>
            )}
          </>
        )}
        {(chapter.status === 'REVIEWED' || chapter.status === 'REVIEW') && (
          <>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
              style={{ border: '1px solid #DDE4EF', color: '#0C1629' }}
            >
              View Chapter
            </Link>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}?edit=1`}
              className="px-3 py-2 rounded-lg font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              Edit
            </Link>
          </>
        )}
        {chapter.status === 'APPROVED' && (
          <Link
            to={`/app/thesis/${id}/chapters/${chapter.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
            style={{ border: '1px solid rgba(22,163,74,0.4)', color: '#16a34a' }}
          >
            <CheckCircle2 style={{ width: 13, height: 13 }} /> View Approved
          </Link>
        )}
        {['APPROVED', 'LOCKED', 'FINALIZED'].includes(chapter.status) && (
          <button
            onClick={onUnapprove}
            disabled={unapproving}
            title="Return this chapter to Draft"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-sans text-xs font-medium transition-colors disabled:opacity-50"
            style={{ border: '1px solid rgba(217,119,6,0.4)', color: '#d97706' }}
          >
            <RotateCcw style={{ width: 13, height: 13 }} /> Unapprove
          </button>
        )}
        {chapter.status === 'GENERATING' && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ border: '1px solid rgba(37,99,235,0.3)' }}>
            <Loader2 style={{ width: 14, height: 14, color: '#2563eb' }} className="animate-spin" />
            <span className="font-sans text-sm text-brand-600">Generating…</span>
          </div>
        )}
        {chapter.status === 'GENERATING' && (
          <button
            onClick={async () => {
              setStopping(true)
              try { await onStop(chapter) } finally { setStopping(false) }
            }}
            disabled={stopping}
            className="flex items-center gap-2 px-3 py-2 rounded-lg font-sans text-xs transition-colors disabled:opacity-50"
            style={{ border: '1px solid rgba(220,38,38,0.4)', color: '#dc2626' }}
            title="Stop generation"
          >
            <Square style={{ width: 11, height: 11 }} /> {stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        {(chapter.status === 'LOCKED' || chapter.status === 'FINALIZED') && (
          <Link
            to={`/app/thesis/${id}/chapters/${chapter.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
            style={{
              border: `1px solid ${chapter.status === 'FINALIZED' ? 'rgba(21,128,61,0.4)' : '#DDE4EF'}`,
              color: chapter.status === 'FINALIZED' ? '#15803d' : '#0C1629',
            }}
          >
            <Lock style={{ width: 13, height: 13 }} /> View {chapter.status === 'FINALIZED' ? 'Final' : 'Chapter'}
          </Link>
        )}
      </div>
    </div>
  )
}

// ─── Special section card ─────────────────────────────────────────────────────

function SpecialCard({ icon: Icon, title, subtitle, buttonLabel, onClick, color = '#4A6080' }) {
  return (
    <div
      className="rounded-xl p-6 flex flex-col"
      style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-center gap-3 mb-2">
        <Icon style={{ width: 18, height: 18, color }} />
        <h3 className="font-sans font-semibold text-acdyon-text" style={{ fontSize: 16 }}>{title}</h3>
      </div>
      <p className="font-sans text-xs text-acdyon-textFaint mb-4">{subtitle}</p>
      <button
        onClick={onClick}
        className="mt-auto flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors self-start"
        style={{ border: `1px solid ${color}50`, color }}
      >
        {buttonLabel} <ChevronRight style={{ width: 13, height: 13 }} />
      </button>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChapterHubPage() {
  const { id } = useParams()
  const { user } = useAuthStore()

  const [thesis,   setThesis]   = useState(null)
  const [chapters, setChapters] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [generating, setGenerating] = useState(null) // chapter being generated

  const load = async () => {
    try {
      const [thesisData, chaptersData] = await Promise.all([
        getThesis(id, user?.id),
        listChapters(id),
      ])
      setThesis(thesisData)
      setChapters(chaptersData || [])
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while any chapter is GENERATING.
  // 15s, not 4s: chapter generation runs for minutes, and polling every 4s
  // burned ~225 requests per 15-minute rate-limit window on its own, which
  // pushed the user over the limit and caused 429s mid-generation.
  const anyGenerating = chapters.some(c => c.status === 'GENERATING')
  useEffect(() => {
    if (!anyGenerating) return
    const timer = setInterval(async () => {
      try {
        const data = await listChapters(id)
        setChapters(data || [])
      } catch { /* */ }
    }, 15000)
    return () => clearInterval(timer)
  }, [anyGenerating, id])

  const handleChapterStatusUpdate = (chapterId, status) => {
    setChapters(prev => prev.map(c => c.id === chapterId ? { ...c, status } : c))
  }

  const handleStopGeneration = async (chapter) => {
    try {
      await api.patch(`/thesis/${id}/chapters/${chapter.id}/status`, { status: 'NOT_STARTED', cancelGeneration: true })
    } catch {
      await supabase.from('chapters').update({ status: 'NOT_STARTED' }).eq('id', chapter.id)
    }
    handleChapterStatusUpdate(chapter.id, 'NOT_STARTED')
  }

  const [approvingAll,   setApprovingAll]   = useState(false)
  const [unapprovingAll, setUnapprovingAll] = useState(false)
  const [selectedIds,    setSelectedIds]    = useState(() => new Set())

  // DRAFT is included: chapters sit in DRAFT after generation, and "approve all"
  // that skipped DRAFT approved nothing on a freshly written thesis. The
  // two-hop below already handles DRAFT -> UNDER_REVIEW -> APPROVED.
  // NOT_STARTED stays out — there is nothing written to approve.
  const APPROVABLE_STATUSES = ['DRAFT', 'REVIEW', 'REVIEWED', 'UNDER_REVIEW']
  const UNAPPROVABLE_STATUSES = ['APPROVED', 'LOCKED', 'FINALIZED']

  // With nothing ticked the buttons act on every eligible chapter, which is how
  // Approve All behaved before selection existed.
  const inSelection = (c) => selectedIds.size === 0 || selectedIds.has(c.id)
  const approvableChapters   = chapters.filter(c => APPROVABLE_STATUSES.includes(c.status) && inSelection(c))
  const unapprovableChapters = chapters.filter(c => UNAPPROVABLE_STATUSES.includes(c.status) && inSelection(c))

  const toggleSelected = (chapterId) => setSelectedIds(prev => {
    const next = new Set(prev)
    next.has(chapterId) ? next.delete(chapterId) : next.add(chapterId)
    return next
  })

  // One chapter failing must never abort the batch.
  const runBatch = async (list, setBusy, step) => {
    setBusy(true)
    const failed = []
    try {
      for (const chapter of list) {
        try { await step(chapter) } catch (err) {
          failed.push(chapter.chapter_number)
          console.error(`Chapter ${chapter.chapter_number}:`, err.message)
        }
      }
    } finally {
      setBusy(false)
      setSelectedIds(new Set())
    }
    if (failed.length) toast.error(`Could not update chapter(s): ${failed.join(', ')}`)
  }

  const approveChapter = async (chapter) => {
    let current = chapter.status
    // The workflow engine only allows APPROVED from UNDER_REVIEW, so DRAFT and
    // the legacy review states hop through it first.
    if (current !== 'UNDER_REVIEW') {
      await applyWorkflowTransition({
        thesisId: id, chapterId: chapter.id,
        currentStatus: current, targetStatus: 'UNDER_REVIEW',
        reason: 'Bulk approve — moving to review before approval',
      })
      current = 'UNDER_REVIEW'
    }
    await applyWorkflowTransition({
      thesisId: id, chapterId: chapter.id,
      currentStatus: current, targetStatus: 'APPROVED',
      reason: 'Bulk approve',
    })
    handleChapterStatusUpdate(chapter.id, 'APPROVED')
  }

  // APPROVED -> DRAFT is already permitted by ALLOWED_TRANSITIONS; LOCKED and
  // FINALIZED route back through APPROVED first.
  const unapproveChapter = async (chapter) => {
    let current = chapter.status
    if (current === 'LOCKED' || current === 'FINALIZED') {
      await applyWorkflowTransition({
        thesisId: id, chapterId: chapter.id,
        currentStatus: current, targetStatus: 'APPROVED',
        reason: 'Unapprove — unlocking before returning to draft',
      })
      current = 'APPROVED'
    }
    await applyWorkflowTransition({
      thesisId: id, chapterId: chapter.id,
      currentStatus: current, targetStatus: 'DRAFT',
      reason: 'Unapprove',
    })
    handleChapterStatusUpdate(chapter.id, 'DRAFT')
  }


  // ── Post-generation review ────────────────────────────────────────────────
  // Streams progress per chapter. Rewrites that would damage citations or
  // statistics are rejected server-side and reported as flagged, so a chapter
  // can come back with issues listed and nothing changed — that is correct.

  const [reviewing,     setReviewing]     = useState(false)
  const [reviewStatus,  setReviewStatus]  = useState('')
  const [reviewReport,  setReviewReport]  = useState(null)

  const handleReviewThesis = async () => {
    setReviewing(true)
    setReviewReport(null)
    setReviewStatus('Starting review…')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/thesis/${id}/review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      )
      if (resp.status === 409) { toast.error('A review is already running for this thesis.'); return }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)

      const reader  = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const done  = []

      while (true) {
        const { done: finished, value } = await reader.read()
        if (finished) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n'); buffer = lines.pop()
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let evt
          try { evt = JSON.parse(line.slice(6)) } catch { continue }

          if (evt.error)    throw new Error(evt.error)
          if (evt.status === 'started') setReviewStatus(`Reviewing ${evt.totalChapters} chapters…`)
          if (evt.progress) setReviewStatus(`Reviewing Chapter ${evt.progress.chapter}: ${evt.progress.title}`)
          if (evt.chapterDone) done.push(evt.chapterDone)
          if (evt.done) {
            setReviewReport({ summary: evt.summary, report: evt.report || done })
            const { issuesFound, issuesFixed, fixesSkipped } = evt.summary
            toast.success(
              issuesFound === 0
                ? 'Review complete — no issues found.'
                : `Review complete — ${issuesFound} issue(s) found, ${issuesFixed} fixed`
                  + (fixesSkipped ? `, ${fixesSkipped} left for you (a safe correction was not possible)` : '') + '.',
              { duration: 8000 }
            )
            await load()
          }
        }
      }
    } catch (err) {
      console.error('[review]', err)
      toast.error('Review failed: ' + (err.message || 'unknown error'))
    } finally {
      setReviewing(false)
      setReviewStatus('')
    }
  }

  const handleApproveAll   = () => runBatch(approvableChapters,   setApprovingAll,   approveChapter)
  const handleUnapproveAll = () => runBatch(unapprovableChapters, setUnapprovingAll, unapproveChapter)
  const handleUnapproveOne = (chapter) => runBatch([chapter], setUnapprovingAll, unapproveChapter)

  const handleExportThesis = () => {
    if (!thesis) return
    const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')

    const css = `
      @page { size: A4; margin: 2.5cm 3cm; }
      * { box-sizing: border-box; }
      body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.8; color: #000; background: #fff; margin: 0; }
      .page { width: 100%; padding: 0; margin: 0 auto; }
      h1 { font-size: 16pt; font-weight: bold; margin-top: 0; }
      h2 { font-size: 14pt; font-weight: bold; margin-top: 2em; }
      h3 { font-size: 12pt; font-weight: bold; font-style: italic; margin-top: 1.5em; }
      h4 { font-size: 12pt; font-weight: bold; margin-top: 1.25em; }
      p { margin-bottom: 1em; text-align: justify; }
      table { width: 100%; border-collapse: collapse; margin: 1.5em 0; font-size: 11pt; }
      td, th { border: 1px solid #999; padding: .4em .6em; }
      th { background: #f0f0f0; font-weight: bold; }
      .chapter-break { page-break-before: always; }
      .front-page { display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; min-height: 29.7cm; page-break-after: always; }
      .toc-chapter { display: flex; gap: .5em; padding: .3em 0; font-size: 11pt; font-weight: bold; }
      .toc-section { display: flex; gap: .5em; padding: .15em 0; margin-left: 1.5em; font-size: 10.5pt; font-weight: normal; }
      .toc-sub { display: flex; gap: .5em; padding: .1em 0; margin-left: 3em; font-size: 10pt; font-weight: normal; color: #333; }
      .toc-dots { flex: 1; border-bottom: 1px dotted #888; margin-bottom: .3em; min-width: 20px; }
      .no-print { position: fixed; top: 16px; right: 16px; z-index: 9999; display: flex; gap: 8px; }
      .no-print button { padding: 8px 16px; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; font-family: system-ui, sans-serif; }
      .btn-print { background: #2563eb; color: #fff; }
      .btn-close { background: #e2e8f0; color: #0f172a; }
      @media print { .no-print { display: none !important; } .chapter-break { page-break-before: always; } }
    `

    const frontMatter = `
      <div class="front-page">
        <div style="margin-top:4cm">
          <h1 style="font-size:20pt;font-weight:bold;margin-bottom:.5em">${esc(thesis.title)}</h1>
          ${thesis.subtitle ? `<p style="font-size:13pt;margin:.5em 0">${esc(thesis.subtitle)}</p>` : ''}
        </div>
        <div style="margin-top:3cm;font-size:12pt">
          <p>A thesis submitted in partial fulfilment of the requirements</p>
          <p>for the degree of Doctor of Philosophy</p>
        </div>
        <div style="margin-top:2.5cm;font-size:12pt">
          ${thesis.programme ? `<p>${esc(thesis.programme)}</p>` : ''}
          ${thesis.university ? `<p>${esc(thesis.university)}</p>` : ''}
          ${thesis.department ? `<p>${esc(thesis.department)}</p>` : ''}
        </div>
        <div style="margin-top:2cm;font-size:12pt"><p>${new Date().getFullYear()}</p></div>
      </div>`

    const tocRows = chapters.map(ch => {
      const secs = Array.isArray(ch.toc_sections?.sections) ? ch.toc_sections.sections
                 : Array.isArray(ch.toc_sections) ? ch.toc_sections : []
      const secRows = secs.map(sec => `
        <div class="toc-section">
          <span style="min-width:44px;shrink:0">${esc(sec.number)}</span>
          <span style="flex:1">${esc(sec.title)}</span>
          <span class="toc-dots"></span>
        </div>
        ${(sec.subsections || []).map(sub => `
          <div class="toc-sub">
            <span style="min-width:56px;shrink:0">${esc(sub.number)}</span>
            <span style="flex:1">${esc(sub.title)}</span>
            <span class="toc-dots"></span>
          </div>`).join('')}
      `).join('')
      return `
        <div class="toc-chapter">
          <span style="min-width:44px;shrink:0">Ch ${ch.chapter_number}</span>
          <span style="flex:1">${esc(ch.title)}</span>
          <span class="toc-dots"></span>
        </div>
        ${secRows}`
    }).join('')

    const tocPage = `
      <div class="chapter-break">
        <h1 style="text-align:center;margin-bottom:1.5em">Table of Contents</h1>
        ${tocRows}
      </div>`

    const chapPages = chapters.map(ch => `
      <div class="chapter-break">
        <h1 style="text-align:center;margin-bottom:1.5em">Chapter ${ch.chapter_number}: ${esc(ch.title)}</h1>
        ${ch.content || '<p><em>Chapter not yet written.</em></p>'}
      </div>`).join('')

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${esc(thesis.title)}</title>
<style>${css}</style>
</head>
<body>
<div class="no-print">
  <button class="btn-print" onclick="window.print()">🖨 Save as PDF</button>
  <button class="btn-close" onclick="window.close()">✕ Close</button>
</div>
${frontMatter}${tocPage}${chapPages}
</body>
</html>`

    const win = window.open('', '_blank')
    if (!win) { alert('Allow pop-ups for this site to export the thesis.'); return }
    win.document.write(html)
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 800)
  }

  // Deep link from the Files page (#reference-documents): the sections are far down the page.
  useEffect(() => {
    const target = window.location.hash.slice(1)
    if (loading || !target) return
    const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    return () => clearTimeout(t)
  }, [loading])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  const totalWords    = chapters.reduce((s, c) => s + (c.word_count || 0), 0)
  const totalPages    = Math.round(totalWords / 250)
  const WRITTEN_STATUSES = ['DRAFT', 'REVIEW', 'REVIEWED', 'APPROVED', 'FINALIZED', 'LOCKED', 'REGEN_PENDING']
  const chapWritten   = chapters.filter(c => WRITTEN_STATUSES.includes(c.status)).length
  const pct           = Math.min(Math.round((totalPages / 200) * 100), 100)

  return (
    <>
      {/* Chapter generation slide-over */}
      {generating && (
        <GenerateSlideOver
          chapter={generating}
          onClose={() => setGenerating(null)}
          onGenerated={handleChapterStatusUpdate}
        />
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes chapterPulse {
          0%, 100% { border-color: rgba(37,99,235,0.4); }
          50%       { border-color: rgba(37,99,235,1); }
        }
      `}</style>

      <div className="space-y-8 max-w-6xl">

        {/* ── Header ──────────────────────────────────────────────── */}
        {/* Title stacks above the actions rather than sharing a row with them —
            a side-by-side layout squeezed the title column down to a sliver
            every time another bulk-action button was added, wrapping a long
            thesis title into 8+ lines. The button row wraps on its own now,
            so it can grow without touching the title's width. */}
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            <h1
              className="font-display text-acdyon-text"
              style={{ fontSize: '2.25rem', lineHeight: 1.15 }}
            >
              {thesis?.title || 'My Thesis'}
            </h1>
            {thesis && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {[thesis.university, thesis.programme, thesis.methodology].filter(Boolean).map(v => (
                  <span
                    key={v}
                    className="font-mono text-xs px-2 py-0.5 rounded"
                    style={{ background: 'rgba(37,99,235,0.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.2)' }}
                  >
                    {v}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {approvableChapters.length > 0 && (
              <button
                onClick={handleApproveAll}
                disabled={approvingAll}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ border: '1px solid rgba(22,163,74,0.4)', background: 'rgba(22,163,74,0.07)', color: '#16a34a' }}
              >
                {approvingAll
                  ? <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Approving…</>
                  : <><CheckCheck style={{ width: 14, height: 14 }} /> {selectedIds.size > 0 ? 'Approve Selected' : 'Approve All'} ({approvableChapters.length})</>}
              </button>
            )}
            {chapters.some(c => (c.word_count || 0) > 0) && (
              <button
                onClick={handleReviewThesis}
                disabled={reviewing}
                title="Re-read every written chapter and correct anomalies"
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ border: '1px solid rgba(37,99,235,0.4)', background: 'rgba(37,99,235,0.07)', color: '#2563eb' }}
              >
                {reviewing
                  ? <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Reviewing…</>
                  : <><ShieldCheck style={{ width: 14, height: 14 }} /> Review Thesis</>}
              </button>
            )}
            {unapprovableChapters.length > 0 && (
              <button
                onClick={handleUnapproveAll}
                disabled={unapprovingAll}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ border: '1px solid rgba(217,119,6,0.4)', background: 'rgba(217,119,6,0.07)', color: '#d97706' }}
              >
                {unapprovingAll
                  ? <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Returning…</>
                  : <><RotateCcw style={{ width: 14, height: 14 }} /> {selectedIds.size > 0 ? 'Unapprove Selected' : 'Unapprove All'} ({unapprovableChapters.length})</>}
              </button>
            )}
            <Link
              to={`/app/thesis/${id}/toc`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              <BookOpen style={{ width: 14, height: 14 }} /> View ToC
            </Link>
            <Link
              to={`/app/thesis/${id}/front-matter`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              <FileText style={{ width: 14, height: 14 }} /> Front Matter
            </Link>
            <Link
              to={`/app/thesis/${id}/export`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-colors"
              style={{ border: '1px solid rgba(37,99,235,0.4)', background: 'rgba(37,99,235,0.07)', color: '#2563eb' }}
            >
              <Download style={{ width: 14, height: 14 }} /> Export Thesis
            </Link>
          </div>
        </div>

        {/* ── Stats bar ───────────────────────────────────────────── */}
        <div
          className="grid grid-cols-4 gap-0 rounded-xl overflow-hidden"
          style={{ background: '#F4F7FC', border: '1px solid #DDE4EF' }}
        >
          {[
            { label: 'Total Words',     value: totalWords.toLocaleString() },
            { label: 'Estimated Pages', value: totalPages },
            { label: 'Chapters Written', value: `${chapWritten} / ${chapters.length}` },
          ].map(({ label, value }, i) => (
            <div
              key={label}
              className="flex flex-col items-center justify-center py-5"
              style={{ borderRight: '1px solid #DDE4EF' }}
            >
              <p className="font-mono text-2xl font-semibold text-brand-600">{value}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-1">{label}</p>
            </div>
          ))}
          {/* Progress ring */}
          <div className="flex flex-col items-center justify-center py-5 gap-1">
            <ProgressRing pct={pct} size={52} />
            <p className="font-mono text-sm font-semibold" style={{ color: pct >= 100 ? '#3DB87A' : '#2563eb' }}>{pct}%</p>
            <p className="font-sans text-xs text-acdyon-textFaint">of 200 pages</p>
          </div>
        </div>

        {/* ── Chapter grid ─────────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Chapters</h2>
          {(reviewing || reviewReport) && (
            <div className="mb-5 rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck style={{ width: 15, height: 15, color: '#2563eb' }} />
                <span className="font-sans text-sm font-semibold" style={{ color: '#0C1629' }}>Thesis Review</span>
                {reviewing && <span className="font-sans text-xs" style={{ color: '#506280' }}>{reviewStatus}</span>}
              </div>

              {reviewReport && (
                <>
                  <div className="font-sans text-xs mb-3" style={{ color: '#506280' }}>
                    {reviewReport.summary.chaptersReviewed} chapter(s) reviewed ·{' '}
                    {reviewReport.summary.issuesFound} issue(s) found ·{' '}
                    {reviewReport.summary.issuesFixed} corrected
                    {reviewReport.summary.fixesSkipped > 0 && (
                      <> · <span style={{ color: '#d97706' }}>{reviewReport.summary.fixesSkipped} left for you</span></>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {reviewReport.report.map(entry => (
                      <div key={entry.chapterId} className="rounded-lg p-3" style={{ background: '#F7F9FC', border: '1px solid #E6ECF5' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs" style={{ color: '#2563eb' }}>CH {String(entry.chapterNumber).padStart(2, '0')}</span>
                          <span className="font-sans text-xs font-medium" style={{ color: '#0C1629' }}>{entry.title}</span>
                          <span className="font-sans text-xs ml-auto" style={{ color: entry.issues.length ? '#d97706' : '#16a34a' }}>
                            {entry.issues.length ? `${entry.issues.length} issue(s)` : 'clean'}
                            {entry.applied ? ' · corrected' : ''}
                          </span>
                        </div>

                        {/* A rejected rewrite is not a failure to hide: the defect is
                            real, but applying the model's version would have altered
                            citations or numbers, so the text was left alone. */}
                        {entry.skipped?.length > 0 && (
                          <div className="flex items-start gap-1.5 font-sans text-xs mb-1" style={{ color: '#d97706' }}>
                            <AlertTriangle style={{ width: 12, height: 12, marginTop: 2, flexShrink: 0 }} />
                            <span>{entry.skipped.length} correction(s) not applied — {entry.skipped[0].reason}. The text was left as written.</span>
                          </div>
                        )}
                        {entry.error && (
                          <div className="font-sans text-xs" style={{ color: '#dc2626' }}>{entry.error}</div>
                        )}

                        {entry.issues.map((issue, i) => (
                          <div key={i} className="font-sans text-xs mt-1.5" style={{ color: '#506280' }}>
                            <span className="font-mono" style={{ color: issue.severity === 'high' ? '#dc2626' : '#506280' }}>
                              [{issue.type}]
                            </span>{' '}
                            {issue.explanation}
                            {issue.quote && <em style={{ color: '#8B9AB0' }}> — “{issue.quote}”</em>}
                            {issue.fixed ? <span style={{ color: '#16a34a' }}> ✓ fixed</span> : <span style={{ color: '#d97706' }}> · needs your attention</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {chapters.map(chapter => (
              <ChapterCard
                key={chapter.id}
                chapter={chapter}
                onGenerate={setGenerating}
                onStop={handleStopGeneration}
                selected={selectedIds.has(chapter.id)}
                onToggleSelect={() => toggleSelected(chapter.id)}
                onUnapprove={() => handleUnapproveOne(chapter)}
                unapproving={unapprovingAll}
              />
            ))}
          </div>
        </div>

        {/* ── Rebuild intelligence ──────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Rebuild Thesis Intelligence</h2>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520 }}
          >
            <p className="font-sans text-xs text-acdyon-textFaint mb-3">
              Scans all existing chapter content and rebuilds the Thesis Memory layer without
              regenerating any prose. Run this after importing chapters or to refresh intelligence
              for a thesis you started before memory tracking was enabled.
            </p>
            <ReindexPanel thesisId={id} />
          </div>
        </div>

        {/* ── Reference upload ─────────────────────────────────────── */}
        <div>
          <h2 id="reference-list" className="font-sans font-semibold text-acdyon-text mb-4" style={{ scrollMarginTop: 16 }}>Reference List</h2>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520 }}
          >
            <p className="font-sans text-xs text-acdyon-textFaint mb-3">
              Paste your APA 7th-edition bibliography or import a .txt / .bib file. To base the thesis on your own DOCX / PDF files, upload them under Reference Documents below. This enables Citation
              Integrity audits and Literature Ecology analysis. Uploading replaces the existing
              reference list for this thesis.
            </p>
            <ReferenceUploadPanel thesisId={id} />
          </div>
        </div>

        {/* ── Research sources ──────────────────────────────────────── */}
        <div>
          <h2 id="reference-documents" className="font-sans font-semibold text-acdyon-text mb-1" style={{ scrollMarginTop: 16 }}>Reference Documents &amp; Research Sources</h2>
          <p className="font-sans text-xs text-acdyon-textFaint mb-4">
            Upload the reference documents your thesis should be based on (DOCX, DOC, PDF, TXT),
            plus your SOP, synopsis, proposal, supervisor comments or drafts. Every chapter is
            written from the passages of these documents that match each section.
          </p>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520 }}
          >
            <ResearchSourcesPanel thesisId={id} />
          </div>
        </div>

        {/* ── Thesis memory ─────────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Thesis Memory</h2>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520, marginBottom: '2rem' }}
          >
            <p className="font-sans text-xs text-acdyon-textFaint mb-3">
              Tracks theoretical positions, methodology, variables, hypotheses, and chapter summaries
              to maintain consistency across all generated chapters.
            </p>
            <ThesisMemoryPanel thesisId={id} />
          </div>
        </div>

        {/* ── Citation registry ─────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Citation Integrity</h2>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520 }}
          >
            <p className="font-sans text-xs text-acdyon-textFaint mb-3">
              Scans all generated chapters, extracts every in-text APA citation, and cross-references
              against your thesis reference list to detect mismatches or hallucinated sources.
            </p>
            <CitationAuditPanel thesisId={id} />
          </div>
        </div>

        {/* ── Literature ecology ────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Literature Ecology</h2>
          <div
            className="rounded-xl p-5"
            style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', maxWidth: 520 }}
          >
            <p className="font-sans text-xs text-acdyon-textFaint mb-3">
              Controls how citations distribute across chapter types — core scholars recur naturally,
              citation density varies by section, and over-diversification is prevented.
            </p>
            <LiteratureEcologyPanel thesisId={id} />
          </div>
        </div>

        {/* ── Special sections ─────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Supporting Sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SpecialCard
              icon={FileText}
              title="Front Matter"
              subtitle="Title page, abstract, acknowledgements, declaration"
              buttonLabel="Generate Front Matter"
              color="#4C9BC9"
            />
            <SpecialCard
              icon={BookMarked}
              title="References"
              subtitle="Manage your bibliography and citation library"
              buttonLabel="Manage References"
              color="#3DB87A"
            />
            <SpecialCard
              icon={Paperclip}
              title="Appendices"
              subtitle="Research instruments, data tables, supplementary material"
              buttonLabel="Add Appendices"
              color="#9B4CC9"
            />
          </div>
        </div>

      </div>
    </>
  )
}
